import React from 'react'
import EyeIcon from 'assets/icons/eye-2-line-icon'
import CheckIcon from 'assets/icons/check-icon'
import ReturnDraftIcon from 'assets/icons/return-draft-icon'
import EditIcon from 'assets/icons/edit-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import RevertIcon from 'assets/icons/revert-icon'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_PROPOSED_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from '../siteStatuses'
import { getEditOnlyContext, getEditOnlyStatusId } from '../../utils/editOnlyContext'
import { createNavigateReviewDetails, createNavigateRfpStyleViewSite, handleGetMonitoring } from './navigationHandlers'
import { buildMonitoringEvaluationUrl } from 'routing/constants'

function buildMonitoringEvaluationItems(
  site,
  profile,
  history,
  isMySite,
  setMonitoringForDeleting,
  setMonitoringForReverting
) {
  const items = []

  // View monitoring evaluation
  const siteEvaluationsSorted = Array.isArray(site?.monitoring)
    ? [...site.monitoring].sort((a, b) => Number(b.id) - Number(a.id))
    : []
  const confirmedMonitoring = siteEvaluationsSorted.find(
    (entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID
  )
  if (confirmedMonitoring) {
    items.push({
      icon: <EyeIcon />,
      title: 'viewMonitoringEvaluation',
      disabled: false,
      onClick: () => {
        handleGetMonitoring(site.id, confirmedMonitoring.id, history)
      }
    })
  }

  // Edit monitoring evaluation
  const siteEvaluations = site?.monitoring
  const isBirdLifeSecretariatTargetEvaluation =
    Array.isArray(siteEvaluations) &&
    siteEvaluations.find((entry) => {
      return entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })
  if (isBirdLifeSecretariatTargetEvaluation) {
    items.push({
      icon: <EditIcon />,
      title: 'editMonitoringEvaluation',
      disabled: false,
      onClick: () => {
        localStorage.setItem('isEdit', 'false')
        const monitoringId =
          isBirdLifeSecretariatTargetEvaluation.id || isBirdLifeSecretariatTargetEvaluation.monitoringId
        history(buildMonitoringEvaluationUrl(site.id, monitoringId))
      }
    })
  }

  // Remove monitoring
  let removeTargetEvaluation = null
  if (Array.isArray(siteEvaluations)) {
    removeTargetEvaluation = siteEvaluations.find((entry) => {
      return entry && entry.monitor_users_id === profile.id && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })
  }
  if (isMySite && removeTargetEvaluation) {
    items.push({
      icon: <RemoveIcon />,
      title: 'removeMonitoring',
      disabled: false,
      onClick: () => {
        setMonitoringForDeleting(removeTargetEvaluation)
      }
    })
  }

  // Revert submission
  let revertTargetEvaluation = null
  if (siteEvaluationsSorted.length > 0) {
    revertTargetEvaluation = siteEvaluationsSorted.find((entry) => {
      return entry && entry.monitoring_status === SITE_STATUS_CONFIRMED_ID
    })
  }
  const isBirdLifeSecretariatTargetEvaluationForRevert =
    siteEvaluationsSorted.length > 0 &&
    siteEvaluationsSorted.find((entry) => {
      return entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
    })
  if (revertTargetEvaluation && !isBirdLifeSecretariatTargetEvaluationForRevert) {
    items.push({
      icon: <RevertIcon />,
      title: 'revertSubmission',
      disabled: false,
      onClick: () => {
        setMonitoringForReverting(revertTargetEvaluation)
      }
    })
  }

  return items
}

function buildProposedOrSubmittedMenuItems(
  site,
  history,
  approveCallBack,
  declineCallBack,
  approveEditOnlyCallBack,
  profile,
  isMySite,
  setMonitoringForDeleting,
  setMonitoringForReverting
) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)

  return [
    {
      icon: <EyeIcon />,
      title: editOnlySiteStatus ? 'reviewEditOnlySite' : 'review',
      onClick: createNavigateReviewDetails(site, history, editOnlySite, editOnlySiteStatus)
    },
    {
      icon: <CheckIcon />,
      title: editOnlySiteStatus ? 'approveEditOnlySite' : 'approveSite',
      onClick: () => {
        if (editOnlySiteStatus && approveEditOnlyCallBack) {
          approveEditOnlyCallBack({ ...site, isApproved: true })
          return
        }
        approveCallBack(site)
      }
    },
    {
      icon: <ReturnDraftIcon />,
      title: editOnlySiteStatus ? 'returnToEditOnly' : 'returnToDraft',
      onClick: () => {
        if (editOnlySiteStatus && approveEditOnlyCallBack) {
          approveEditOnlyCallBack({ ...site, isApproved: false })
          return
        }
        declineCallBack(site)
      }
    },
    ...buildMonitoringEvaluationItems(
      site,
      profile,
      history,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting
    )
  ]
}

function buildRfpBrcViewOnlyItem(site, history, profile, isMySite, setMonitoringForDeleting, setMonitoringForReverting) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <EyeIcon />,
      title: 'viewSite',
      onClick: createNavigateRfpStyleViewSite(site, history, editOnlySite, editOnlySiteStatus)
    },
    ...buildMonitoringEvaluationItems(
      site,
      profile,
      history,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting
    )
  ]
}

export function buildRfpAndBrcMenuItems({
  site,
  history,
  approveCallBack,
  declineCallBack,
  approveEditOnlyCallBack,
  profile,
  isMySite,
  setMonitoringForDeleting,
  setMonitoringForReverting
}) {
  const statusId = Number(getEditOnlyStatusId(site) ?? site?.SiteStatus?.id ?? site?.SiteStatusId)
  if (
    statusId === SITE_STATUS_PROPOSED_ID ||
    statusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  ) {
    return buildProposedOrSubmittedMenuItems(
      site,
      history,
      approveCallBack,
      declineCallBack,
      approveEditOnlyCallBack,
      profile,
      isMySite,
      setMonitoringForDeleting,
      setMonitoringForReverting
    )
  }
  return buildRfpBrcViewOnlyItem(site, history, profile, isMySite, setMonitoringForDeleting, setMonitoringForReverting)
}

export function getRfpAndBrcActionMenuItems(...args) {
  const [
    site,
    history,
    approveCallBack,
    declineCallBack,
    ,
    ,
    approveEditOnlyCallBack,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting,
    profile
  ] = args
  return buildRfpAndBrcMenuItems({
    site,
    history,
    approveCallBack,
    declineCallBack,
    approveEditOnlyCallBack,
    profile,
    isMySite,
    setMonitoringForDeleting,
    setMonitoringForReverting
  })
}
